import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, Copy, Check, RefreshCw, Smartphone, ShieldAlert, ExternalLink, AlertTriangle,
} from 'lucide-react';
import api from '../../services/api';

/** Abonnement iCalendar d'un médecin à son propre agenda. */
interface AbonnementCalendrier {
  id: number;
  medecin_nom: string;
  url_flux: string;
  url_webcal: string;
  is_active: boolean;
  rappel_minutes: number;
  afficher_nom_patient: boolean;
  jours_passes: number;
  jours_futurs: number;
  derniere_consultation: string | null;
  nombre_rdv: number;
}

interface Props {
  onClose: () => void;
}

const RAPPELS = [
  { valeur: 0, label: 'Aucune alarme' },
  { valeur: 15, label: '15 minutes avant' },
  { valeur: 30, label: '30 minutes avant' },
  { valeur: 60, label: '1 heure avant' },
  { valeur: 120, label: '2 heures avant' },
  { valeur: 1440, label: '1 jour avant' },
];

export default function CalendrierSync({ onClose }: Props) {
  const queryClient = useQueryClient();
  const [copie, setCopie] = useState(false);
  const [confirmationRegen, setConfirmationRegen] = useState(false);

  const { data: abonnement, isLoading, error } = useQuery<AbonnementCalendrier>({
    queryKey: ['abonnement-calendrier'],
    queryFn: async () => (await api.get('/planning/mon-calendrier/')).data,
    retry: false,
  });

  const majMutation = useMutation({
    mutationFn: (donnees: Partial<AbonnementCalendrier>) =>
      api.patch('/planning/mon-calendrier/', donnees),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['abonnement-calendrier'] }),
  });

  const regenererMutation = useMutation({
    mutationFn: () => api.post('/planning/mon-calendrier/regenerer/'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['abonnement-calendrier'] });
      setConfirmationRegen(false);
    },
  });

  const copier = async () => {
    if (!abonnement) return;
    try {
      await navigator.clipboard.writeText(abonnement.url_flux);
      setCopie(true);
      setTimeout(() => setCopie(false), 2500);
    } catch {
      // Le presse-papiers peut être refusé (contexte non sécurisé) : on laisse
      // l'utilisateur sélectionner le champ à la main.
    }
  };

  const messageErreur =
    (error as any)?.response?.data?.error ||
    (error ? "Impossible de charger votre abonnement calendrier." : null);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto p-4" onClick={onClose}>
      <div
        className="relative mx-auto my-8 w-full max-w-2xl bg-white rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Synchroniser mon agenda</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Recevez vos rendez-vous et leurs rappels dans Google Agenda, Outlook ou l'agenda de votre téléphone.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-40 bg-gray-100 rounded" />
              <div className="h-12 w-full bg-gray-100 rounded-xl" />
              <div className="h-24 w-full bg-gray-100 rounded-xl" />
            </div>
          ) : messageErreur ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              {messageErreur}
            </div>
          ) : abonnement ? (
            <>
              {/* URL d'abonnement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Votre adresse d'abonnement privée
                </label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={abonnement.url_flux}
                    onFocus={(e) => e.currentTarget.select()}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-700 text-sm font-mono"
                  />
                  <button
                    onClick={copier}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap ${
                      copie ? 'bg-emerald-600 text-white' : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {copie ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copie ? 'Copié' : 'Copier'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {abonnement.nombre_rdv} rendez-vous dans le flux
                  {abonnement.derniere_consultation
                    ? ` — dernière lecture par votre agenda le ${new Date(
                        abonnement.derniere_consultation
                      ).toLocaleString('fr-FR')}`
                    : " — jamais lu pour l'instant"}
                </p>
              </div>

              {/* Avertissement confidentialité */}
              <div className="flex gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  Cette adresse donne accès à votre agenda sans mot de passe : ne la partagez pas.
                  Les rendez-vous seront copiés sur les serveurs de Google ou Microsoft — si le nom
                  des patients ne doit pas y figurer, décochez l'option ci-dessous.
                </p>
              </div>

              {/* Mode d'emploi */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                  <Smartphone className="h-4 w-4 text-gray-400" /> Comment l'ajouter
                </h3>

                <details className="rounded-xl border border-gray-200 overflow-hidden" open>
                  <summary className="px-4 py-2.5 bg-gray-50 cursor-pointer text-sm font-medium text-gray-700">
                    Google Agenda
                  </summary>
                  <ol className="px-4 py-3 space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
                    <li>
                      Ouvrez{' '}
                      <a
                        href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-700 underline inline-flex items-center gap-0.5"
                      >
                        Ajouter par URL <ExternalLink className="h-3 w-3" />
                      </a>{' '}
                      sur un ordinateur.
                    </li>
                    <li>Collez l'adresse ci-dessus, puis cliquez sur « Ajouter l'agenda ».</li>
                    <li>
                      L'agenda apparaît ensuite dans l'application mobile Google Agenda, avec ses rappels.
                    </li>
                  </ol>
                  <div className="px-4 pb-3">
                    <div className="flex gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span>
                        Google relit les agendas externes à son propre rythme, souvent toutes les
                        8 à 24 heures. Un rendez-vous pris le jour même peut donc n'apparaître
                        qu'avec du retard : gardez le planning de l'application comme référence.
                      </span>
                    </div>
                  </div>
                </details>

                <details className="rounded-xl border border-gray-200 overflow-hidden">
                  <summary className="px-4 py-2.5 bg-gray-50 cursor-pointer text-sm font-medium text-gray-700">
                    Outlook / Microsoft 365
                  </summary>
                  <ol className="px-4 py-3 space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
                    <li>Dans Outlook sur le web : Calendrier → « Ajouter un calendrier ».</li>
                    <li>Choisissez « S'abonner à partir du web » et collez l'adresse.</li>
                    <li>Nommez-le, puis validez par « Importer ».</li>
                  </ol>
                  <p className="px-4 pb-3 text-xs text-gray-500">
                    Outlook rafraîchit en général toutes les 1 à 3 heures.
                  </p>
                </details>

                <details className="rounded-xl border border-gray-200 overflow-hidden">
                  <summary className="px-4 py-2.5 bg-gray-50 cursor-pointer text-sm font-medium text-gray-700">
                    iPhone / iPad (Apple Calendrier)
                  </summary>
                  <ol className="px-4 py-3 space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
                    <li>Réglages → Applications → Calendrier → Comptes → Ajouter un compte.</li>
                    <li>« Autre » → « Ajouter un abonnement calendrier ».</li>
                    <li>Collez l'adresse et validez.</li>
                  </ol>
                  <p className="px-4 pb-3 text-xs text-gray-500">
                    Depuis un iPhone, ce lien ouvre directement l'application :{' '}
                    <a href={abonnement.url_webcal} className="text-teal-700 underline break-all">
                      s'abonner
                    </a>
                  </p>
                </details>
              </div>

              {/* Réglages */}
              <div className="space-y-4 pt-2 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800 pt-3">Réglages</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Alarme sur chaque rendez-vous
                    </label>
                    <select
                      value={abonnement.rappel_minutes}
                      onChange={(e) => majMutation.mutate({ rappel_minutes: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                    >
                      {RAPPELS.map((r) => (
                        <option key={r.valeur} value={r.valeur}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Horizon affiché (jours)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={730}
                      defaultValue={abonnement.jours_futurs}
                      onBlur={(e) => {
                        const valeur = Number(e.target.value);
                        if (valeur !== abonnement.jours_futurs) {
                          majMutation.mutate({ jours_futurs: valeur });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={abonnement.afficher_nom_patient}
                    onChange={(e) =>
                      majMutation.mutate({ afficher_nom_patient: e.target.checked })
                    }
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>
                    <span className="block text-sm text-gray-700">
                      Afficher le nom du patient dans l'agenda
                    </span>
                    <span className="block text-xs text-gray-500">
                      Décoché, seuls le motif et l'horaire sont transmis à Google ou Microsoft.
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={abonnement.is_active}
                    onChange={(e) => majMutation.mutate({ is_active: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>
                    <span className="block text-sm text-gray-700">Abonnement actif</span>
                    <span className="block text-xs text-gray-500">
                      Désactivé, l'adresse cesse immédiatement de répondre.
                    </span>
                  </span>
                </label>
              </div>

              {/* Révocation */}
              <div className="pt-2 border-t border-gray-100">
                {confirmationRegen ? (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                    <p className="text-sm text-red-800">
                      Générer une nouvelle adresse rendra l'actuelle inutilisable. Vous devrez
                      refaire l'abonnement dans chaque agenda où vous l'avez ajoutée. Continuer ?
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => regenererMutation.mutate()}
                        disabled={regenererMutation.isPending}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {regenererMutation.isPending ? 'Génération…' : 'Oui, régénérer'}
                      </button>
                      <button
                        onClick={() => setConfirmationRegen(false)}
                        className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmationRegen(true)}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-red-700"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Générer une nouvelle adresse (révoque l'actuelle)
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
